<template>
  <div
    class="lg:tw-min-w-0 tw-relative tw-z-[100] tw-mb-4 tw-w-full lg:tw-mb-0 lg:tw-flex lg:tw-flex-1 lg:tw-items-center lg:tw-gap-[16px]"
    :class="
      enablePropertyTypeFilters ? 'lg:tw-max-w-[460px]' : 'lg:tw-max-w-[680px]'
    "
  >
    <span
      class="tw-hidden tw-text-sm tw-font-semibold tw-text-[#3B4143] lg:tw-inline lg:tw-whitespace-nowrap"
      >Select list</span
    >
    <div class="lg:tw-min-w-0 tw-relative tw-w-full lg:tw-flex-1">
      <button
        type="button"
        class="tw-flex tw-h-[44px] tw-w-full tw-flex-[1_0_0] tw-items-center tw-justify-between tw-gap-[8px] tw-rounded-full tw-border tw-border-[#D1D5DB] tw-bg-white tw-px-[16px] tw-py-2 tw-text-left tw-text-[16px] tw-text-[#3B4143] focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-[#61B3E5] disabled:tw-bg-white disabled:tw-opacity-100"
        aria-haspopup="listbox"
        :aria-label="
          activeList ? `Select list: ${activeList.name}` : 'Loading shortlists'
        "
        :aria-expanded="isOpen || isMobileListScreenOpen"
        :disabled="isLoading || !activeList"
        @click="toggleListPicker"
      >
        <ShortlistPickerIcon class="tw-h-6 tw-w-6 tw-shrink-0 lg:tw-hidden" />
        <span v-if="activeList" class="tw-flex-1 tw-truncate">{{
          activeList.name
        }}</span>
        <span
          v-else
          class="tw-h-5 tw-flex-1 tw-rounded-full tw-bg-gray-100"
          aria-hidden="true"
        ></span>
        <ChevronDown
          class="tw-ml-auto tw-hidden tw-h-[18px] tw-w-[18px] tw-shrink-0 lg:tw-block"
        />
        <ChevronRight
          class="tw-h-[18px] tw-w-[18px] tw-shrink-0 lg:tw-hidden"
        />
      </button>

      <div
        v-if="isOpen"
        class="shortlist-listbox tw-absolute tw-left-0 tw-top-[60px] tw-hidden tw-w-full tw-max-w-full tw-flex-col tw-gap-[16px] tw-rounded-[28px] tw-bg-white tw-p-[16px] tw-shadow-[0_8px_30px_rgba(0,0,0,0.14)] lg:tw-flex"
        role="listbox"
        aria-label="Shortlists"
      >
        <div v-for="list in lists" :key="list.hash_id" class="tw-min-h-[42px]">
          <form
            v-if="editingHash === list.hash_id"
            class="tw-w-full tw-py-2"
            @submit.prevent="saveRename(list)"
          >
            <label
              :for="`edit-shortlist-${list.hash_id}`"
              class="tw-mb-2 tw-block tw-text-[14px] tw-font-semibold tw-text-[#3B4143]"
            >
              Edit shortlist name
            </label>
            <div class="tw-flex tw-items-center tw-gap-3">
              <input
                :id="`edit-shortlist-${list.hash_id}`"
                v-model="editingName"
                class="shortlist-desktop-name-edit-input tw-h-[48px] tw-flex-1 tw-rounded-[16px] tw-border tw-border-[#D1D5DB] tw-bg-white tw-px-3 tw-text-[15px] tw-text-[#3B4143] focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-[#61B3E5]"
                aria-label="Shortlist name"
                maxlength="80"
                autofocus
              />
              <button
                type="button"
                class="shortlist-list-icon"
                :aria-label="`Cancel editing ${list.name}`"
                title="Cancel"
                @click="cancelRename"
              >
                <CloseIcon />
              </button>
              <button
                type="submit"
                class="shortlist-list-icon"
                :aria-label="`Save shortlist name ${editingName}`"
                title="Save shortlist name"
                :disabled="isSaving || !editingName.trim()"
              >
                <CircleCheck />
              </button>
            </div>
          </form>

          <div
            v-else
            class="tw-group tw-flex tw-min-h-[42px] tw-items-center tw-gap-[16px] tw-rounded-[8px]"
          >
            <button
              type="button"
              class="tw-min-w-0 tw-flex-1 tw-truncate tw-text-left tw-text-[16px] tw-text-[#3B4143] hover:tw-text-[#006DAA] group-hover:tw-text-[#006DAA]"
              role="option"
              :aria-selected="activeListHash === list.hash_id"
              @click="selectList(list.hash_id)"
            >
              {{ list.name }}
            </button>
            <button
              type="button"
              class="shortlist-list-icon group-hover:tw-text-[#006DAA]"
              :aria-label="`Rename ${list.name}`"
              title="Rename shortlist"
              @click="beginRename(list)"
            >
              <Pencil class="shortlist-list-icon-pencil" />
            </button>
            <button
              type="button"
              class="shortlist-list-icon group-hover:tw-text-[#006DAA]"
              :aria-label="`Delete ${list.name}`"
              title="Delete shortlist"
              @click="beginDelete(list)"
            >
              <CircleMinus />
            </button>
          </div>
        </div>

        <div>
          <button
            type="button"
            class="tw-flex tw-min-h-[42px] tw-w-full tw-items-center tw-gap-3 tw-rounded-[8px] tw-px-1 tw-text-left tw-text-[16px] tw-text-[#3B4143] hover:tw-text-[#006DAA]"
            role="option"
            :aria-selected="activeListHash === recentlyAddedHash"
            @click="selectList(recentlyAddedHash)"
          >
            Recently added
          </button>
        </div>

        <div class="tw-border-t tw-border-[#E5E7EB] tw-pt-3">
          <button
            type="button"
            class="tw-flex tw-h-[52px] tw-w-full tw-items-center tw-gap-[10px] tw-self-stretch tw-rounded-[8px] tw-bg-[#EDF3FB] tw-px-4 tw-py-2 tw-text-left tw-text-[16px] tw-font-semibold tw-text-[#006DAA]"
            @click="beginCreate"
          >
            <PlusIcon class="tw-h-[24px] tw-w-[24px] tw-shrink-0" />
            Create new shortlist
          </button>
        </div>
      </div>
    </div>
    <p
      v-if="errorMessage && !isCreating && !deleteHash"
      class="tw-mb-0 tw-mt-2 tw-text-sm tw-text-red-700 lg:tw-absolute lg:tw-left-[92px] lg:tw-top-full"
      role="alert"
    >
      {{ errorMessage }}
    </p>
    <MountingPortal mount-to="#PortalMountTo" append>
      <div
        v-if="isMobileListScreenOpen"
        class="tw-fixed tw-inset-0 tw-z-[10000] tw-flex tw-flex-col tw-bg-white lg:tw-hidden"
        role="dialog"
        aria-modal="true"
        aria-labelledby="shortlist-list-screen-title"
        @keydown.esc.stop.prevent="closeMobileListScreen"
      >
        <header
          class="header-bg bg-nav-blue tw-relative tw-flex tw-h-[72px] tw-shrink-0 tw-items-center tw-justify-center"
        >
          <button
            type="button"
            class="tw-absolute tw-left-[20px] tw-flex tw-h-[40px] tw-w-[40px] tw-items-center tw-justify-center tw-rounded-full tw-border-2 tw-border-white tw-text-white focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-white"
            aria-label="Back to shortlist results"
            @click="closeMobileListScreen"
          >
            <ChevronLeft class="tw-h-[24px] tw-w-[24px]" stroke-width="2" />
          </button>
          <img
            class="tw-h-[40px] tw-w-auto"
            src="/cdn/svg/unihomes-logo.svg"
            alt="UniHomes"
          />
        </header>

        <div class="tw-flex-1 tw-overflow-y-auto tw-p-[16px] tw-pb-8">
          <div class="tw-pb-[16px]">
            <h2
              id="shortlist-list-screen-title"
              class="tw-font-inter tw-m-0 tw-text-[18px] tw-font-extrabold tw-leading-normal tw-text-[#3B4143]"
            >
              Your shortlists
            </h2>
            <p
              class="tw-font-inter tw-mb-0 tw-mt-[8px] tw-text-[16px] tw-font-normal tw-leading-[24px] tw-text-[#4B5563]"
            >
              Switch, rename or create a shortlist.
            </p>
          </div>

          <p
            v-if="errorMessage && !isCreating && !deleteHash"
            class="tw-mb-0 tw-mt-4 tw-text-[14px] tw-text-red-700"
            role="alert"
          >
            {{ errorMessage }}
          </p>

          <div class="tw-mx-[-20px] tw-mt-[16px]">
            <div
              v-for="list in lists"
              :key="`mobile-${list.hash_id}`"
              class="tw-relative tw-flex tw-flex-col tw-items-start tw-gap-[10px] tw-self-stretch tw-border-b tw-border-[#E5E7EB] tw-p-[16px]"
            >
              <form
                v-if="editingHash === list.hash_id"
                class="tw-flex tw-h-[44px] tw-w-full tw-items-center tw-gap-3"
                @submit.prevent="saveRename(list)"
              >
                <input
                  :id="`mobile-edit-shortlist-${list.hash_id}`"
                  v-model="editingName"
                  class="shortlist-name-edit-input tw-max-w-[275px] tw-flex-1 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-[#61B3E5]"
                  :aria-label="`Edit ${list.name}`"
                  maxlength="80"
                />
                <div
                  class="tw-ml-auto tw-flex tw-shrink-0 tw-items-center tw-gap-[8px]"
                >
                  <button
                    type="button"
                    class="shortlist-edit-icon-control focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-[#61B3E5]"
                    :aria-label="`Cancel editing ${list.name}`"
                    :disabled="isSaving"
                    @click="cancelRename"
                  >
                    <CloseIcon class="tw-h-[24px] tw-w-[24px]" />
                  </button>
                  <button
                    type="submit"
                    class="shortlist-edit-icon-control focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-[#61B3E5]"
                    :aria-label="`Save shortlist name ${editingName}`"
                    :disabled="isSaving || !editingName.trim()"
                  >
                    <CircleCheck class="tw-h-[20px] tw-w-[20px]" />
                  </button>
                </div>
              </form>

              <template v-else>
                <div
                  class="tw-relative tw-flex tw-h-[42px] tw-w-full tw-items-center tw-gap-[12px]"
                >
                  <ShortlistListIcon
                    class="tw-h-[24px] tw-w-[24px] tw-shrink-0 tw-text-[#3B4143]"
                  />
                  <button
                    type="button"
                    class="tw-min-w-0 tw-flex-1 tw-truncate tw-text-left tw-text-[14px] tw-text-[#3B4143]"
                    :aria-pressed="activeListHash === list.hash_id"
                    @click="selectList(list.hash_id)"
                  >
                    {{ list.name }}
                  </button>
                  <button
                    type="button"
                    class="tw-flex tw-h-[40px] tw-w-[40px] tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-text-[#3B4143] focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-[#61B3E5]"
                    :aria-label="`Actions for ${list.name}`"
                    :aria-expanded="openActionsHash === list.hash_id"
                    @click.stop="toggleListActions(list.hash_id)"
                  >
                    <MoreVertical class="tw-h-[24px] tw-w-[24px]" />
                  </button>
                  <div
                    v-if="openActionsHash === list.hash_id"
                    class="tw-absolute tw-right-[15px] tw-top-[42px] tw-z-10 tw-inline-flex tw-flex-col tw-items-start tw-gap-[16px] tw-rounded-[24px] tw-bg-white tw-p-[24px] tw-shadow-[0_2px_4px_0_rgba(0,0,0,0.10)]"
                    role="menu"
                  >
                    <button
                      type="button"
                      class="tw-flex tw-h-[24px] tw-items-center tw-gap-[16px] tw-whitespace-nowrap tw-text-left tw-text-[16px] tw-leading-[24px] tw-text-[#3B4143]"
                      role="menuitem"
                      @click="
                        beginRename(list);
                        openActionsHash = null;
                      "
                    >
                      <Pencil class="tw-shrink-0" />
                      <span>Edit name</span>
                    </button>
                    <button
                      type="button"
                      class="tw-flex tw-h-[24px] tw-items-center tw-gap-[16px] tw-whitespace-nowrap tw-text-left tw-text-[16px] tw-leading-[24px] tw-text-[#3B4143]"
                      role="menuitem"
                      @click="beginDelete(list)"
                    >
                      <Trash
                        class="tw-flex tw-h-[18px] tw-w-[18px] tw-shrink-0 tw-items-center tw-justify-center"
                        stroke-color="var(--Mono-Default-text, #3B4143)"
                        aria-hidden="true"
                      />
                      <span>Remove shortlist</span>
                    </button>
                    <button
                      type="button"
                      class="tw-flex tw-h-[24px] tw-items-center tw-gap-[16px] tw-whitespace-nowrap tw-text-left tw-text-[16px] tw-leading-[24px] tw-text-[#3B4143]"
                      role="menuitem"
                      @click="selectList(list.hash_id)"
                    >
                      <Eye class="tw-shrink-0" />
                      <span>View shortlist</span>
                    </button>
                  </div>
                </div>
              </template>
            </div>

            <div
              class="tw-flex tw-flex-col tw-items-start tw-gap-[10px] tw-self-stretch tw-border-b tw-border-[#E5E7EB] tw-p-[16px]"
            >
              <button
                type="button"
                class="tw-flex tw-h-[42px] tw-w-full tw-items-center tw-gap-[12px] tw-text-left tw-text-[14px] tw-text-[#3B4143]"
                :aria-pressed="activeListHash === recentlyAddedHash"
                @click="selectList(recentlyAddedHash)"
              >
                <ShortlistListIcon
                  class="tw-h-[24px] tw-w-[24px] tw-shrink-0 tw-text-[#3B4143]"
                />
                <span>Recently added</span>
              </button>
            </div>
          </div>

          <button
            type="button"
            class="tw-font-inter tw-mt-[36px] tw-flex tw-h-[50px] tw-w-full tw-items-center tw-justify-center tw-gap-[10px] tw-rounded-[58px] tw-bg-[#F48831] tw-px-4 tw-py-2 tw-text-center tw-text-[16px] tw-font-bold tw-leading-[24px] tw-text-white hover:tw-bg-[#E16B0C]"
            @click="beginCreate"
          >
            <CirclePlus class="tw-h-[20px] tw-w-[20px] tw-shrink-0" />
            Create new shortlist
          </button>
        </div>
      </div>

      <div
        v-if="isCreating"
        class="tw-fixed tw-inset-0 tw-z-[10010] tw-flex tw-items-end tw-justify-center tw-bg-[rgba(0,0,0,0.4)] tw-px-0 tw-pb-0 tw-pt-4 lg:tw-items-center lg:tw-p-4"
        role="presentation"
        @click.self="cancelCreate"
        @keydown.esc="cancelCreate"
      >
        <form
          class="tw-flex tw-w-full tw-flex-col tw-items-start tw-overflow-hidden tw-rounded-t-[24px] tw-bg-white tw-shadow-[0_4px_24px_rgba(0,0,0,0.4)] lg:tw-max-w-[495px] lg:tw-rounded-[24px]"
          role="dialog"
          aria-modal="true"
          aria-labelledby="create-shortlist-title"
          @submit.prevent="createList"
        >
          <header
            class="tw-relative tw-flex tw-h-[60px] tw-flex-col tw-items-start tw-justify-center tw-self-stretch tw-bg-[#00A2E1] tw-px-4 tw-py-0 tw-text-white lg:tw-h-auto lg:tw-flex-row lg:tw-items-center lg:tw-justify-between lg:tw-py-[24px] lg:tw-pl-[24px] lg:tw-pr-[32px]"
          >
            <h2
              id="create-shortlist-title"
              class="tw-font-inter tw-m-0 tw-text-[16px] tw-font-semibold tw-leading-[24px] tw-text-white lg:tw-text-[20px]"
            >
              Creating shortlist
            </h2>
            <button
              type="button"
              class="tw-absolute tw-right-4 tw-top-1/2 tw-flex tw-h-[24px] tw-w-[24px] tw--translate-y-1/2 tw-items-center tw-justify-center tw-gap-[10px] tw-rounded-full tw-p-[10px] tw-text-white focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-white lg:tw-static lg:tw-right-auto lg:tw-top-auto lg:tw-translate-y-0"
              aria-label="Close create shortlist dialog"
              :disabled="isSaving"
              @click="cancelCreate"
            >
              <ModalCloseIcon class="tw-h-[18px] tw-w-[18px] tw-shrink-0" />
            </button>
          </header>

          <div
            class="tw-mb-[16px] tw-flex tw-flex-col tw-items-center tw-gap-[16px] tw-self-stretch tw-p-[16px] lg:tw-gap-8 lg:tw-p-[24px]"
          >
            <div class="tw-flex tw-w-full tw-flex-col">
              <label
                for="create-shortlist-name"
                class="tw-font-inter tw-mb-[8px] tw-block tw-text-[16px] tw-font-bold tw-leading-[24px] tw-text-[#3B4143]"
              >
                List name
              </label>
              <input
                id="create-shortlist-name"
                ref="createNameInput"
                v-model="creatingName"
                class="shortlist-list-name-input tw-h-[40px] tw-w-full tw-rounded-[10px] tw-border tw-border-[#D1D5DB] tw-bg-white tw-px-3 tw-text-[16px] tw-text-[#3B4143] focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-[#61B3E5]"
                aria-label="New shortlist name"
                placeholder="My shortlist and mates"
                maxlength="80"
                :disabled="isSaving"
              />
              <p
                v-if="errorMessage"
                class="tw-mb-0 tw-mt-2 tw-text-[13px] tw-text-red-700"
                role="alert"
              >
                {{ errorMessage }}
              </p>
            </div>
          </div>

          <footer
            class="tw-flex tw-w-full tw-flex-col tw-items-center tw-justify-center tw-gap-[10px] tw-self-stretch tw-border-t tw-border-[#E5E7EB] tw-bg-white tw-p-[16px] lg:tw-flex-row lg:tw-justify-end lg:tw-gap-4 lg:tw-p-[24px]"
          >
            <button
              type="submit"
              class="tw-w-full tw-rounded-full tw-bg-[#F48831] tw-px-5 tw-py-2 tw-text-[14px] tw-font-semibold tw-text-white hover:tw-bg-[#E16B0C] focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-[#61B3E5] disabled:tw-cursor-not-allowed disabled:tw-bg-[#D1D5DB] lg:tw-order-2 lg:tw-w-auto"
              :disabled="isSaving || !creatingName.trim()"
            >
              {{ isSaving ? 'Creating…' : 'Create list' }}
            </button>
            <button
              type="button"
              class="tw-w-full tw-rounded-full tw-px-4 tw-py-2 tw-text-[14px] tw-font-semibold tw-text-[#006DAA] hover:tw-bg-[#F3F7FB] focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-[#61B3E5] lg:tw-order-1 lg:tw-w-auto"
              :disabled="isSaving"
              @click="cancelCreate"
            >
              Cancel
            </button>
          </footer>
        </form>
      </div>
      <div
        v-if="deleteHash && deleteListTarget"
        class="tw-fixed tw-inset-0 tw-z-[10010] tw-flex tw-items-end tw-justify-center tw-bg-[rgba(0,0,0,0.4)] tw-px-0 tw-pb-0 tw-pt-4 lg:tw-items-center lg:tw-p-4"
        role="presentation"
        @click.self="cancelDelete"
        @keydown.esc="cancelDelete"
      >
        <div
          class="tw-flex tw-w-full tw-flex-col tw-items-start tw-overflow-hidden tw-rounded-t-[24px] tw-bg-white tw-shadow-[0_4px_24px_rgba(0,0,0,0.4)] lg:tw-max-w-[495px] lg:tw-rounded-[24px]"
          role="dialog"
          aria-modal="true"
          aria-labelledby="remove-shortlist-title"
        >
          <header
            class="tw-relative tw-flex tw-h-[60px] tw-flex-col tw-items-start tw-justify-center tw-self-stretch tw-bg-[#00A2E1] tw-px-4 tw-py-0 tw-text-white lg:tw-h-auto lg:tw-flex-row lg:tw-items-center lg:tw-justify-between lg:tw-py-[24px] lg:tw-pl-[24px] lg:tw-pr-[32px]"
          >
            <h2
              id="remove-shortlist-title"
              class="tw-font-inter tw-m-0 tw-text-[16px] tw-font-semibold tw-leading-[24px] tw-text-white lg:tw-text-[20px]"
            >
              Remove shortlist
            </h2>
            <button
              type="button"
              class="tw-absolute tw-right-4 tw-top-1/2 tw-flex tw-h-[24px] tw-w-[24px] tw--translate-y-1/2 tw-items-center tw-justify-center tw-gap-[10px] tw-rounded-full tw-p-[10px] tw-text-white focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-white lg:tw-static lg:tw-right-auto lg:tw-top-auto lg:tw-translate-y-0"
              aria-label="Close remove shortlist dialog"
              :disabled="isSaving"
              @click="cancelDelete"
            >
              <ModalCloseIcon class="tw-h-[18px] tw-w-[18px] tw-shrink-0" />
            </button>
          </header>

          <div
            class="tw-flex tw-flex-col tw-items-center tw-gap-[16px] tw-self-stretch tw-px-[16px] tw-py-[24px] lg:tw-gap-8 lg:tw-p-[24px]"
          >
            <div class="tw-w-full">
              <h3
                class="tw-font-inter tw-mb-[6px] tw-mt-0 tw-text-[24px] tw-font-bold tw-leading-[36px] tw-text-[#3B4143] lg:tw-text-[24px] lg:tw-font-bold lg:tw-leading-normal"
              >
                Are you sure?
              </h3>
              <p
                class="tw-font-inter tw-mb-0 tw-text-[16px] tw-font-normal tw-leading-[24px] tw-text-[#6B7280] lg:tw-text-[16px] lg:tw-leading-[20px]"
              >
                Deleting
                <strong
                  class="tw-font-normal tw-text-[#6B7280] lg:tw-font-semibold lg:tw-text-[#3B4143]"
                >
                  “{{ deleteListTarget.name }}”
                </strong>
                will remove all properties saved to it. This action can’t be
                undone.
              </p>
              <p
                v-if="errorMessage"
                class="tw-mb-0 tw-mt-2 tw-text-[13px] tw-text-red-700"
                role="alert"
              >
                {{ errorMessage }}
              </p>
            </div>
          </div>

          <footer
            class="tw-flex tw-w-full tw-flex-col tw-items-center tw-justify-center tw-gap-[10px] tw-self-stretch tw-border-t tw-border-[#E5E7EB] tw-bg-white tw-p-[16px] lg:tw-flex-row lg:tw-justify-end lg:tw-gap-4 lg:tw-p-[24px]"
          >
            <button
              type="button"
              class="tw-w-full tw-rounded-full tw-bg-[#F48831] tw-px-5 tw-py-2 tw-text-[14px] tw-font-semibold tw-text-white hover:tw-bg-[#E16B0C] focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-[#61B3E5] disabled:tw-cursor-not-allowed disabled:tw-bg-[#D1D5DB] lg:tw-order-2 lg:tw-w-auto"
              :disabled="isSaving"
              @click="deleteList(deleteListTarget)"
            >
              {{ isSaving ? 'Deleting…' : 'Delete' }}
            </button>
            <button
              type="button"
              class="tw-w-full tw-rounded-full tw-px-4 tw-py-2 tw-text-[14px] tw-font-semibold tw-text-[#006DAA] hover:tw-bg-[#F3F7FB] focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-[#61B3E5] lg:tw-order-1 lg:tw-w-auto"
              :disabled="isSaving"
              @click="cancelDelete"
            >
              Cancel
            </button>
          </footer>
        </div>
      </div>
    </MountingPortal>
  </div>
</template>

<script>
import api from '@js/api/authenticatedApi';
import CircleCheck from './SVG/CircleCheck.vue';
import CirclePlus from './SVG/CirclePlus.vue';
import CloseIcon from './SVG/CloseIcon.vue';
import Eye from './SVG/Eye.vue';
import Pencil from './SVG/Pencil.vue';
import Trash from './SVG/Trash.vue';
import ChevronDown from '@components/Shared/Icons/ChevronDown.vue';
import ChevronLeft from '@components/Shared/Icons/ChevronLeft.vue';
import ChevronRight from '@components/Shared/Icons/ChevronRight.vue';
import CircleMinus from '@components/Shared/Icons/CircleMinus.vue';
import ModalCloseIcon from '@components/Shared/Icons/ModalCloseIcon.vue';
import MoreVertical from '@components/Shared/Icons/MoreVertical.vue';
import PlusIcon from '@components/Shared/Icons/PlusIcon.vue';
import ShortlistListIcon from '@components/Shared/Icons/ShortlistListIcon.vue';
import ShortlistPickerIcon from '@components/Shared/Icons/ShortlistPickerIcon.vue';

const RECENTLY_ADDED_HASH = 'recently-added';

export default {
  name: 'ShortlistListsSelector',
  components: {
    CircleCheck,
    CirclePlus,
    CloseIcon,
    Eye,
    Pencil,
    Trash,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    CircleMinus,
    ModalCloseIcon,
    MoreVertical,
    PlusIcon,
    ShortlistListIcon,
    ShortlistPickerIcon,
  },
  props: {
    location: {
      type: Object,
      default: () => ({}),
    },
    enablePropertyTypeFilters: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['selection-change', 'load-error'],
  data() {
    return {
      lists: [],
      activeListHash: null,
      isOpen: false,
      isMobileListScreenOpen: false,
      openActionsHash: null,
      isLoading: false,
      isSaving: false,
      isCreating: false,
      creatingName: '',
      editingHash: null,
      editingName: '',
      deleteHash: null,
      errorMessage: '',
      recentlyAddedHash: RECENTLY_ADDED_HASH,
      previousBodyOverflow: '',
    };
  },
  computed: {
    activeList() {
      if (!this.activeListHash) {
        return null;
      }

      if (this.activeListHash === RECENTLY_ADDED_HASH) {
        return { hash_id: RECENTLY_ADDED_HASH, name: 'Recently added' };
      }

      return (
        this.lists.find((list) => list.hash_id === this.activeListHash) || {
          hash_id: RECENTLY_ADDED_HASH,
          name: 'Recently added',
        }
      );
    },
    locationLabel() {
      return [this.location.area_name, this.location.city_name]
        .filter(Boolean)
        .join(', ');
    },
    deleteListTarget() {
      return (
        this.lists.find((list) => list.hash_id === this.deleteHash) || null
      );
    },
  },
  watch: {
    isMobileListScreenOpen(isOpen) {
      if (isOpen) {
        this.previousBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = this.previousBodyOverflow;
      }
    },
  },
  beforeDestroy() {
    if (this.isMobileListScreenOpen) {
      document.body.style.overflow = this.previousBodyOverflow;
    }
  },
  async mounted() {
    await this.loadLists();
  },
  methods: {
    async loadLists() {
      this.isLoading = true;
      this.errorMessage = '';

      try {
        const response = await api.get('/account/shortlist-lists');
        this.lists = response.data.lists || [];
        this.activeListHash =
          response.data.active_list_hash || RECENTLY_ADDED_HASH;
        await this.loadSelection();
      } catch (_error) {
        this.errorMessage =
          'Your shortlists could not be loaded. Please try again.';
        this.$emit('load-error');
      } finally {
        this.isLoading = false;
      }
    },
    async loadSelection() {
      const path =
        this.activeListHash === RECENTLY_ADDED_HASH
          ? '/account/shortlist-lists/recently-added/properties'
          : `/account/shortlist-lists/${encodeURIComponent(
              this.activeListHash
            )}/properties`;
      const response = await api.get(path);
      this.$emit('selection-change', {
        list: this.activeList,
        properties: response.data.shortlisted || [],
        readOnly: false,
      });
    },
    toggleListPicker() {
      if (window.innerWidth < 1024) {
        this.isMobileListScreenOpen = !this.isMobileListScreenOpen;
        this.isOpen = false;
        this.openActionsHash = null;
        return;
      }

      this.isOpen = !this.isOpen;
      this.isMobileListScreenOpen = false;
    },
    closeMobileListScreen() {
      this.isMobileListScreenOpen = false;
      this.openActionsHash = null;
      this.cancelRename();
    },
    toggleListActions(hash) {
      this.openActionsHash = this.openActionsHash === hash ? null : hash;
    },
    async selectList(hash) {
      this.errorMessage = '';
      this.isSaving = true;

      try {
        await api.post(
          `/account/shortlist-lists/${encodeURIComponent(hash)}/select`
        );
        this.activeListHash = hash;
        await this.loadSelection();
        this.isOpen = false;
        this.isMobileListScreenOpen = false;
        this.openActionsHash = null;
      } catch (_error) {
        this.errorMessage =
          'This shortlist could not be opened. Please try again.';
      } finally {
        this.isSaving = false;
      }
    },
    beginCreate() {
      this.errorMessage = '';
      this.isCreating = true;
      if (!this.isMobileListScreenOpen) this.isOpen = false;
      this.creatingName = this.locationLabel;
      this.$nextTick(() => this.$refs.createNameInput?.focus());
    },
    cancelCreate() {
      this.isCreating = false;
      this.creatingName = '';
      this.errorMessage = '';
    },
    beginDelete(list) {
      this.errorMessage = '';
      this.deleteHash = list.hash_id;
      this.openActionsHash = null;
      if (!this.isMobileListScreenOpen) this.isOpen = false;
    },
    cancelDelete() {
      if (this.isSaving) return;

      this.deleteHash = null;
      this.errorMessage = '';
    },
    async createList() {
      const name = this.creatingName.trim();
      if (!name) return;

      this.isSaving = true;
      this.errorMessage = '';
      const payload = { name };
      if (this.location.city_name) payload.city_name = this.location.city_name;
      if (this.location.area_name) payload.area_name = this.location.area_name;

      try {
        const response = await api.post('/account/shortlist-lists', payload);
        this.lists.push(response.data.list);
        this.activeListHash = response.data.list.hash_id;
        this.cancelCreate();
        await this.loadSelection();
        this.isOpen = false;
        this.isMobileListScreenOpen = false;
      } catch (_error) {
        this.errorMessage =
          'The shortlist could not be created. Please try again.';
      } finally {
        this.isSaving = false;
      }
    },
    beginRename(list) {
      this.errorMessage = '';
      this.editingHash = list.hash_id;
      this.editingName = list.name;
    },
    cancelRename() {
      this.editingHash = null;
      this.editingName = '';
    },
    async saveRename(list) {
      const name = this.editingName.trim();
      if (!name) return;

      this.isSaving = true;
      this.errorMessage = '';
      try {
        const response = await api.patch(
          `/account/shortlist-lists/${encodeURIComponent(list.hash_id)}`,
          { name }
        );
        Object.assign(list, response.data.list);
        this.cancelRename();
      } catch (_error) {
        this.errorMessage =
          'The shortlist could not be renamed. Please try again.';
      } finally {
        this.isSaving = false;
      }
    },
    async deleteList(list) {
      this.isSaving = true;
      this.errorMessage = '';
      try {
        const response = await api.delete(
          `/account/shortlist-lists/${encodeURIComponent(list.hash_id)}`
        );
        this.lists = this.lists.filter((item) => item.hash_id !== list.hash_id);
        this.deleteHash = null;

        if (this.activeListHash === list.hash_id) {
          this.activeListHash =
            response.data.active_list_hash || RECENTLY_ADDED_HASH;
          await this.loadSelection();
        }
      } catch (_error) {
        this.errorMessage =
          'The shortlist could not be deleted. Please try again.';
      } finally {
        this.isSaving = false;
      }
    },
    async reloadSelection() {
      await this.loadSelection();
    },
    async removeProperty(propertyId) {
      if (this.activeListHash === RECENTLY_ADDED_HASH) return false;

      await api.delete(
        `/account/shortlist-lists/${encodeURIComponent(
          this.activeListHash
        )}/properties/${encodeURIComponent(propertyId)}`
      );
      await this.loadSelection();
      return true;
    },
  },
};
</script>

<style scoped>
.shortlist-list-icon {
  display: flex;
  width: 42px;
  height: 42px;
  padding: 9px;
  flex: 0 0 42px;
  aspect-ratio: 1 / 1;
  justify-content: center;
  align-items: center;
  color: #3b4143;
}

.shortlist-name-edit-input {
  box-sizing: border-box;
  min-width: 0;
  height: 44px;
  padding: 9px 16px;
  border: 1px solid #d1d5db;
  border-radius: 16px;
  background: #fff;
  color: #6b7280;
  leading-trim: both;
  text-edge: cap;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
}

.shortlist-desktop-name-edit-input {
  min-width: 0;
}

.shortlist-edit-icon-control {
  display: flex;
  width: 42px;
  height: 42px;
  padding: 9px;
  flex: 0 0 42px;
  aspect-ratio: 1 / 1;
  justify-content: center;
  align-items: center;
  color: #3b4143;
}

.shortlist-list-icon svg {
  width: 24px;
  height: 24px;
}

.shortlist-list-icon svg.shortlist-list-icon-pencil {
  width: 19px;
  height: 19px;
}

.shortlist-list-icon:hover,
.shortlist-list-icon:focus-visible {
  color: #006daa;
}

.shortlist-list-icon:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.shortlist-list-name-input::placeholder {
  color: var(--Mono-Grey-500, #6B7280);
  leading-trim: both;
  text-edge: cap;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
}

.shortlist-list-action {
  border-radius: 8px;
  padding: 6px 9px;
  font-size: 13px;
  font-weight: 600;
  color: #3b4143;
}

.shortlist-list-action-primary {
  color: #006daa;
}

.shortlist-list-action-danger {
  color: #b42318;
}

.shortlist-list-action:disabled {
  opacity: 0.5;
}

@media (min-width: 1024px) {
  .shortlist-listbox {
    min-width: 320px;
  }
}
</style>
